import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  User,
  Building2,
  Plus,
  Minus,
  Flag,
  Download,
  Check,
  Loader2,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const icons = [Users, User, Building2];

const HowItWorks = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const { t, lang } = useLang();
  const H = t.howItWorks;
  const plan = H.monthlyPlan;
  const L = H.leadCta;

  const toggle = (i) => setOpenIndex((cur) => (cur === i ? -1 : i));

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setStatus("error");
      setErrorMsg(L.errorInvalid);
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(`${BACKEND}/api/leads/pdf-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          name,
          source: "6-month-plan-pdf",
          lang,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      const url = `${BACKEND}${data.download_url}`;
      const a = document.createElement("a");
      a.href = url;
      a.download =
        lang === "bg"
          ? "velocitas-6-month-plan-bg.pdf"
          : "velocitas-6-month-plan.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(L.errorGeneric);
    }
  };

  return (
    <section
      id="how"
      data-testid="how-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container-vel">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-5" data-testid="how-overline">
            {H.overline}
          </div>
          <h2
            data-testid="how-heading"
            className="font-heading text-4xl sm:text-5xl text-navy font-light leading-[1.05] tracking-tight"
          >
            {H.headlinePre}{" "}
            <em className="not-italic underline decoration-coral decoration-2 underline-offset-8">
              {H.headlineHighlight}
            </em>
            {H.headlinePost}
          </h2>
          <p className="mt-6 font-body text-base sm:text-lg text-ink-muted leading-relaxed font-light">
            {H.rhythmIntro}
          </p>
        </div>

        <div
          data-testid="how-rhythm-grid"
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-navy/10 border border-navy/10"
        >
          {H.rhythm.map((item, i) => {
            const Icon = icons[i] || Users;
            return (
              <motion.div
                key={item}
                data-testid={`how-rhythm-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white p-8 md:p-10 hover:bg-navy-50/40 transition-colors duration-500"
              >
                <div className="flex items-center justify-center w-12 h-12 border border-coral/40 text-coral">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <p className="mt-6 font-heading text-xl sm:text-2xl text-navy leading-snug">
                  {item}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
        >
          <div className="lg:col-span-7">
            <p
              data-testid="how-depth"
              className="font-heading text-3xl sm:text-4xl text-navy font-light leading-tight"
            >
              {H.closing[0]}
              <br />
              <span className="text-coral">{H.closing[1]}</span>
            </p>
          </div>
          <div className="lg:col-span-5 lg:border-l lg:border-navy/15 lg:pl-10">
            <p
              data-testid="how-footnote"
              className="font-body text-sm sm:text-base text-ink-muted leading-relaxed italic"
            >
              {H.footnote}
            </p>
          </div>
        </motion.div>

        {/* Monthly Plan Timeline */}
        <div
          data-testid="how-monthly-plan"
          className="mt-24 md:mt-32 pt-16 border-t border-navy/10"
        >
          <div className="max-w-3xl mb-12">
            <div
              className="overline mb-5"
              data-testid="how-monthly-overline"
            >
              {plan.overline}
            </div>
            <h3
              data-testid="how-monthly-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl text-navy font-light leading-[1.05] tracking-tight"
            >
              {plan.heading}
              <span className="text-coral">.</span>
            </h3>
            <p className="mt-5 font-body text-base sm:text-lg text-ink-muted leading-relaxed font-light">
              {plan.intro}
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-6 top-2 bottom-2 w-px bg-navy/15 pointer-events-none" />

            <div className="space-y-4">
              {plan.months.map((m, i) => {
                const isOpen = openIndex === i;
                const checkpoint = m.isCheckpoint;
                return (
                  <motion.div
                    key={m.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    className="relative pl-12 md:pl-16"
                    data-testid={`monthly-item-${i}`}
                  >
                    <div
                      className={`absolute left-0 md:left-1 top-5 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center border ${
                        checkpoint
                          ? "bg-coral border-coral text-white"
                          : "bg-white border-navy/30 text-navy"
                      } rounded-full z-10`}
                    >
                      {checkpoint ? (
                        <Flag size={16} strokeWidth={1.75} />
                      ) : (
                        <span className="font-heading text-sm font-medium">
                          {String(i < 3 ? i + 1 : i).padStart(2, "0").slice(-2)}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      data-testid={`monthly-toggle-${i}`}
                      aria-expanded={isOpen}
                      className={`w-full text-left group border ${
                        checkpoint
                          ? "border-coral/40 bg-coral/5"
                          : "border-navy/10 bg-white hover:border-navy/30"
                      } transition-colors duration-300`}
                    >
                      <div className="flex items-start justify-between gap-6 p-5 md:p-7">
                        <div className="flex-1 min-w-0">
                          <div
                            className={`font-body text-[11px] font-semibold tracking-[0.22em] uppercase ${
                              checkpoint ? "text-coral" : "text-ink-soft"
                            }`}
                          >
                            {m.label}
                          </div>
                          <div className="mt-2 font-heading text-xl sm:text-2xl text-navy leading-snug">
                            {m.title}
                          </div>
                          <div className="mt-1 font-heading italic text-sm sm:text-base text-ink-muted">
                            {m.tagline}
                          </div>
                        </div>
                        <div
                          className={`shrink-0 mt-1 w-9 h-9 flex items-center justify-center border ${
                            checkpoint
                              ? "border-coral text-coral"
                              : "border-navy/20 text-navy group-hover:border-navy"
                          } transition-colors duration-300`}
                          aria-hidden="true"
                        >
                          {isOpen ? (
                            <Minus size={16} strokeWidth={1.75} />
                          ) : (
                            <Plus size={16} strokeWidth={1.75} />
                          )}
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div
                              className="px-5 md:px-7 pb-6 md:pb-7 border-t border-navy/10"
                              data-testid={`monthly-content-${i}`}
                            >
                              <p className="mt-5 font-body text-sm sm:text-base text-navy/90 leading-relaxed">
                                {m.brief}
                              </p>
                              {m.detail && (
                                <p className="mt-3 font-body text-sm sm:text-base text-ink-muted leading-relaxed">
                                  {m.detail}
                                </p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Lead-capture CTA */}
          <motion.div
            data-testid="plan-cta"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="mt-16 md:mt-20 bg-navy text-white relative overflow-hidden"
          >
            <div
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, #E55A3C 0%, transparent 50%), radial-gradient(circle at 80% 80%, #ffffff 0%, transparent 50%)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 p-8 md:p-12 lg:p-16">
              <div className="lg:col-span-6">
                <div className="font-body text-[11px] font-semibold tracking-[0.22em] uppercase text-coral">
                  {L.overline}
                </div>
                <h4
                  data-testid="plan-cta-heading"
                  className="mt-3 font-heading text-3xl sm:text-4xl font-light leading-tight !text-white"
                >
                  {L.headingLine1}
                  <br />
                  {L.headingLine2}
                </h4>
                <p className="mt-5 font-body text-sm sm:text-base text-white/70 leading-relaxed max-w-md">
                  {L.body}
                </p>
              </div>

              <div className="lg:col-span-6 lg:border-l lg:border-white/15 lg:pl-12 flex flex-col justify-center">
                {status === "success" ? (
                  <div
                    data-testid="plan-cta-success"
                    className="flex items-start gap-4"
                  >
                    <div className="shrink-0 w-10 h-10 border border-coral text-coral flex items-center justify-center">
                      <Check size={18} strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="font-heading text-xl !text-white">
                        {L.successHeading}
                      </div>
                      <p className="mt-2 font-body text-sm text-white/70 leading-relaxed">
                        {L.successBodyPre}{" "}
                        <a
                          data-testid="plan-cta-manual-download"
                          href={`${BACKEND}/api/leads/pdf?lang=${lang}`}
                          className="underline decoration-coral underline-offset-4 hover:text-coral transition-colors"
                        >
                          {L.successBodyLink}
                        </a>
                        {L.successBodyPost}
                      </p>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-testid="plan-cta-form"
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={L.namePlaceholder}
                        data-testid="plan-cta-name"
                        className="bg-transparent border border-white/25 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-coral transition-colors"
                      />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={L.emailPlaceholder}
                        required
                        data-testid="plan-cta-email"
                        className="bg-transparent border border-white/25 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-coral transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="plan-cta-submit"
                      className="group inline-flex items-center justify-between gap-4 w-full bg-coral hover:bg-coral-dark text-white px-6 py-4 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="font-body text-sm font-semibold tracking-wider uppercase">
                        {status === "loading" ? L.submitLoading : L.submitIdle}
                      </span>
                      {status === "loading" ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Download
                          size={18}
                          className="transition-transform group-hover:translate-y-0.5"
                        />
                      )}
                    </button>
                    {status === "error" && (
                      <p
                        data-testid="plan-cta-error"
                        className="font-body text-xs text-coral"
                      >
                        {errorMsg}
                      </p>
                    )}
                    <p className="font-body text-[11px] text-white/40 leading-relaxed">
                      {L.footnote}
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
